import { useEffect, useRef, RefObject } from 'react';

/**
 * useHorizontalDrag
 *
 * Adds mouse drag functionality to scroll horizontally within a container.
 * Useful for horizontal notation strips where users want to drag left/right
 * to navigate through the score.
 *
 * @param containerRef - Reference to the scrollable container element
 * @param enabled - Whether drag functionality is enabled (default: true)
 */
export function useHorizontalDrag(
  containerRef: RefObject<HTMLElement>,
  enabled: boolean = true
) {
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const container = containerRef.current;

    const handleMouseDown = (e: MouseEvent) => {
      // Only drag with left mouse button
      if (e.button !== 0) return;

      // Don't interfere with clickable elements
      const target = e.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.tagName === 'A') return;

      isDraggingRef.current = true;
      startXRef.current = e.pageX - container.offsetLeft;
      scrollLeftRef.current = container.scrollLeft;

      // Change cursor to grabbing
      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';

      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      e.preventDefault();

      const x = e.pageX - container.offsetLeft;
      const walk = (x - startXRef.current) * 1.5; // Multiplier for drag speed
      container.scrollLeft = scrollLeftRef.current - walk;
    };

    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;

      isDraggingRef.current = false;
      container.style.cursor = 'grab';
      container.style.userSelect = '';
    };

    const handleMouseLeave = () => {
      if (!isDraggingRef.current) return;

      isDraggingRef.current = false;
      container.style.cursor = 'grab';
      container.style.userSelect = '';
    };

    // Set initial cursor
    container.style.cursor = 'grab';

    // Add event listeners
    container.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);

    // Cleanup
    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);

      // Reset cursor
      container.style.cursor = '';
      container.style.userSelect = '';
    };
  }, [containerRef, enabled]);
}
